/**
 * Fundraising form: shared domain model.
 *
 * Pure, framework-free types shared by the `add-fundraising` and
 * `edit-fundraising` slices. Both edit the exact same four fields (amount,
 * currency, closing month, closing year), so the whole draft lives here. The
 * month/year option helpers are extracted 1:1 from the flat pages so the
 * listing and both forms render the same labels. No React, no Redux, no fetch.
 *
 * This module depends on NONE of the three fundraising features (hermeticity).
 */
import type { CreateFundraisingRequest, FundraisingResponse } from '@shared/schemas';

import { formatAmountInput, parseAmount } from './amount-input';

/** The editable fundraising fields shared by Add and Edit. */
export interface FundraisingDraft {
  /**
   * The field's text as the client sees it ("2,500,000"); converted to a number
   * only when building the request (`parseAmount`).
   */
  amount: string;
  currency: string;
  /** 1–12. */
  closeMonth: number;
  closeYear: number;
}

/**
 * A pristine draft. Defaults mirror the flat `FundraisingForm` page: USD and
 * the current month/year (the caller passes `new Date()`; kept injectable so
 * tests stay deterministic).
 */
export function makeEmptyDraft(now: Date = new Date()): FundraisingDraft {
  return {
    amount: '',
    currency: 'USD',
    closeMonth: now.getMonth() + 1,
    closeYear: now.getFullYear(),
  };
}

/** A saved round as an editable draft (the card's inline editor and the Edit dialog). */
export function draftFromRound(round: FundraisingResponse): FundraisingDraft {
  return {
    amount: formatAmountInput(round.amount),
    currency: round.currency,
    closeMonth: round.close_month,
    closeYear: round.close_year,
  };
}

/** Field-keyed validation messages produced by `validateDraft`. */
export type ValidationErrors = Partial<Record<keyof FundraisingDraft, string>>;

/**
 * A single field edit; string fields and numeric fields keep their own value
 * type (one variant per field so the reducer narrows on the discriminant).
 */
export type FundraisingFieldChange =
  | { field: 'amount'; value: string }
  | { field: 'currency'; value: string }
  | { field: 'closeMonth'; value: number }
  | { field: 'closeYear'; value: number };

/** Lifecycle of a save (create / update). */
export type SaveStatus = 'idle' | 'saving' | 'succeeded' | 'failed';

/** Lifecycle of an initial load (Edit hydrates from the backend). */
export type LoadStatus = 'idle' | 'loading' | 'loaded' | 'failed';

/**
 * Map the draft to the snake_case create body (also the full-update PATCH
 * body; the flat page sent the same payload for both). The only place the
 * string amount becomes a number.
 */
export function toCreateRequest(draft: FundraisingDraft): CreateFundraisingRequest {
  return {
    amount: parseAmount(draft.amount),
    currency: draft.currency,
    close_month: draft.closeMonth,
    close_year: draft.closeYear,
  };
}

/** Short month labels indexed by `close_month` (1–12; index 0 unused). */
export const MONTH_LABELS = [
  '',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
